'use client'

import {TableBody, TableHead, TableRow} from '~/design-system/foundations/Table'
import {
  Collapsible,
  CollapsibleContent,
  type CollapsibleProps,
  CollapsibleTrigger,
} from '@radix-ui/react-collapsible'
import TextContentCell from './_components/TextContentCell'
import {
  type HgTableCollapsibleTitleRowProps,
  type HgTableCollapsibleSectionProps,
} from './types'
import {Fragment, useEffect, useState} from 'react'
import HgChevronIndicator from '../HgIcon/HgChevronIndicator'

const HgTableCollapsibleTriggerRow = ({
  icon,
  heading,
}: HgTableCollapsibleTitleRowProps) => {
  return (
    <TableRow className="w-full hover:border-t-border-default has-[button[data-state='open']]:border-t-border-default">
      <TableHead scope="row" className="flex w-full">
        <CollapsibleTrigger className="group flex w-full flex-1 items-center justify-between py-[calc(var(--s3)-2px)] outline-2 outline-offset-1 outline-border-focus focus-visible:outline">
          <TextContentCell leftIconType={icon} title={heading} />
          <div className="flex h-32 min-h-32 w-32 min-w-32 items-center justify-center">
            <HgChevronIndicator />
          </div>
        </CollapsibleTrigger>
      </TableHead>
    </TableRow>
  )
}

// Ensure that only valid props are passed to the Fragment - necessary in order to wrap the Collapsible
// table body elements, and to avoid breaking semantic table structure.
const CollapsibleFragment = ({children, key}: CollapsibleProps) => {
  return <Fragment key={key}>{children}</Fragment>
}

export const HgTableCollapsibleSection = ({
  children,
  open = false,
  onOpenChange,
  id,
  ...props
}: HgTableCollapsibleSectionProps) => {
  const [isOpen, setIsOpen] = useState(open)

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (onOpenChange) {
      onOpenChange(open)
    }
  }

  useEffect(() => {
    setIsOpen(open)
  }, [open])

  return (
    <Collapsible asChild open={isOpen} onOpenChange={handleOpenChange}>
      <CollapsibleFragment>
        <TableBody id={id} className="scroll-mt-comparison-table-header-height">
          <HgTableCollapsibleTriggerRow {...props} />
        </TableBody>
        <CollapsibleContent
          asChild
          className="overflow-hidden duration-300 motion-safe:data-[state=closed]:animate-collapseUp motion-safe:data-[state=open]:animate-collapseDown"
        >
          <TableBody>{children}</TableBody>
        </CollapsibleContent>
      </CollapsibleFragment>
    </Collapsible>
  )
}
